import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Cpu } from 'lucide-react';
import { useBookingConfig } from '@/lib/BookingConfigContext';

// Modal rig picker for the walk-in POS. Lists only the rigs applicable to the
// chosen experience (adult rigs for adult sessions, child_sim for junior ones)
// and marks each as FREE or BOOKED at the current walk-in slot so staff can
// only assign a rig that isn't already taken.
export default function WalkInRigPicker({ open, experience, bookedRigKeys, onSelect, onClose }) {
  const { rigs } = useBookingConfig();
  const [specsRig, setSpecsRig] = React.useState(null);

  const applicable = useMemo(() => {
    const all = Object.entries(rigs)
      .map(([key, rig]) => ({ key, ...rig }))
      .filter((r) => r.active !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    // Only show rigs explicitly allowed for this experience
    const allowed = Array.isArray(experience?.allowed_rigs) ? experience.allowed_rigs : [];
    const allowedSet = new Set(allowed);
    return allowed.length ? all.filter((r) => allowedSet.has(r.key)) : all;
  }, [rigs, experience]);

  const bookedSet = new Set(bookedRigKeys || []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.96, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 16 }}
            transition={{ type: 'spring', stiffness: 240, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl bg-[#111115] border border-white/10 overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-primary text-[10px] font-bold tracking-widest">SELECT A FREE RIG</p>
                <h3 className="text-white font-black text-lg uppercase">{experience?.name || 'Choose a rig'}</h3>
                <p className="text-white/40 text-xs mt-0.5">Only rigs not booked at this time slot are selectable.</p>
              </div>
              <button onClick={onClose} className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {applicable.map(({ key, ...rig }) => {
                  const booked = bookedSet.has(key);
                  const hasSpecs = Array.isArray(rig.specs) && rig.specs.filter(Boolean).length > 0;
                  return (
                    <div
                      key={key}
                      className={`relative text-left p-4 border transition-all overflow-hidden ${
                        booked ? 'bg-[#0d0d0f] border-red-500/20 opacity-60' : 'bg-[#0d0d0f] border-white/10 hover:border-primary hover:bg-primary/[0.06]'
                      }`}
                    >
                      {rig.image_url && (
                        <div className="w-full h-24 -mx-4 -mt-4 mb-3 bg-black overflow-hidden">
                          <img src={rig.image_url} alt={rig.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[9px] font-bold tracking-widest text-primary">{rig.tag}</span>
                        <span className={`text-[9px] font-bold tracking-widest px-1.5 py-0.5 ${booked ? 'bg-red-500/15 text-red-300' : 'bg-green-500/15 text-green-300'}`}>
                          {booked ? 'BOOKED' : 'FREE'}
                        </span>
                      </div>
                      <h4 className="text-white font-black text-sm uppercase leading-tight">{rig.name}</h4>
                      <p className="text-white/40 text-xs mt-1 line-clamp-2">{rig.description}</p>

                      {hasSpecs && !booked && (
                        <button
                          type="button"
                          onClick={() => setSpecsRig({ key, ...rig })}
                          className="mt-3 flex items-center gap-1.5 text-primary text-[10px] font-bold tracking-wider hover:text-primary/80"
                        >
                          <Cpu className="w-3 h-3" /> SPECS
                        </button>
                      )}

                      {!booked && (
                        <button
                          type="button"
                          onClick={() => onSelect(key)}
                          className="mt-3 w-full bg-primary text-white font-bold text-[11px] tracking-widest py-2.5 hover:bg-primary/90"
                        >
                          ASSIGN THIS RIG
                        </button>
                      )}
                      {booked && (
                        <p className="mt-3 w-full text-center text-red-300/70 font-bold text-[10px] tracking-widest py-2.5 border border-red-500/20">
                          IN USE
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {applicable.length === 0 && (
                <p className="text-center text-white/40 text-sm py-10">No rigs configured for this experience.</p>
              )}
            </div>
          </motion.div>

          {/* Specs popup */}
          <AnimatePresence>
            {specsRig && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSpecsRig(null)}
                className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
              >
                <motion.div
                  initial={{ scale: 0.94, y: 16 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.94, y: 16 }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative w-full max-w-md bg-[#111115] border border-white/10 overflow-hidden"
                >
                  <button onClick={() => setSpecsRig(null)} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white/60">
                    <X className="w-4 h-4" />
                  </button>
                  {specsRig.image_url && (
                    <div className="w-full aspect-[16/10] bg-black overflow-hidden">
                      <img src={specsRig.image_url} alt={specsRig.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-primary text-[10px] font-bold tracking-widest block mb-1">{specsRig.tag}</span>
                    <h3 className="text-white font-black text-xl uppercase mb-1 pr-8">{specsRig.name}</h3>
                    <div className="space-y-2 mt-4">
                      {specsRig.specs.filter(Boolean).map((spec, i) => (
                        <div key={i} className="flex items-start gap-2.5 text-sm text-white/70 border border-white/5 bg-white/[0.02] px-3.5 py-2.5">
                          <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" strokeWidth={3} />
                          <span>{spec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}